import { useRef, useCallback, useState, type DragEvent } from 'react';
import { Paperclip, X, FileSpreadsheet, Loader2, AlertCircle, Image, FileText, File as FileIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FILE_ACCEPT, isSupportedFile } from '@/services/file-parser';
import type { PendingAttachment } from '@/hooks/use-file-attachments';
import type { ChatMessageAttachment } from '@/types/chat.types';

/* ─── Paperclip Button ─────────────────────────────── */

interface FileAttachmentButtonProps {
  onFiles: (files: FileList) => void;
  disabled?: boolean;
}

export function FileAttachmentButton({ onFiles, disabled }: FileAttachmentButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFiles(e.target.files);
      // Reset so the same file can be re-selected
      e.target.value = '';
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleClick}
        disabled={disabled}
        className="shrink-0 size-8 cursor-pointer hover:bg-accent/60"
        title="Attach file"
      >
        <Paperclip className="size-3.5" />
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={FILE_ACCEPT}
        multiple
        className="hidden"
        onChange={handleChange}
      />
    </>
  );
}

/* ─── Attachment Chip List ─────────────────────────── */

interface AttachmentChipListProps {
  attachments: PendingAttachment[];
  onRemove: (fileId: string) => void;
}

export function AttachmentChipList({ attachments, onRemove }: AttachmentChipListProps) {
  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 px-1 pb-2">
      {attachments.map((att) => (
        <div
          key={att.fileId}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] border ${
            att.status === 'error'
              ? 'bg-destructive/10 border-destructive/20 text-destructive'
              : att.status === 'parsing'
                ? 'bg-muted/50 border-border text-muted-foreground'
                : 'bg-primary/10 border-primary/20 text-foreground'
          }`}
        >
          {att.status === 'parsing' ? (
            <Loader2 className="size-3 animate-spin" />
          ) : att.status === 'error' ? (
            <AlertCircle className="size-3" />
          ) : (
            <AttachmentIcon fileName={att.fileName} />
          )}
          <span className="max-w-[120px] truncate">{att.fileName}</span>
          <button
            type="button"
            onClick={() => onRemove(att.fileId)}
            className="ml-0.5 hover:text-destructive transition-colors"
          >
            <X className="size-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

/* ─── Drag-and-Drop Overlay ────────────────────────── */

interface DropZoneProps {
  onFiles: (files: FileList) => void;
  children: React.ReactNode;
}

export function DropZone({ onFiles, children }: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      if (e.dataTransfer.files.length > 0) {
        // Filter to supported files
        const dt = new DataTransfer();
        for (const file of Array.from(e.dataTransfer.files)) {
          if (isSupportedFile(file.name)) {
            dt.items.add(file);
          }
        }
        if (dt.files.length > 0) {
          onFiles(dt.files);
        }
      }
    },
    [onFiles],
  );

  return (
    <div
      className="relative flex-1 flex flex-col min-h-0 min-w-0"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-primary/5 border-2 border-dashed border-primary/40 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <Paperclip className="size-8 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-primary">
              Drop files here
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Images, text, spreadsheets, PDFs
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Message Attachment Indicator ─────────────────── */

interface MessageAttachmentIndicatorProps {
  attachments: ChatMessageAttachment[];
}

export function MessageAttachmentIndicator({ attachments }: MessageAttachmentIndicatorProps) {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {attachments.map((att) => (
        <span
          key={att.fileId}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-white/20"
        >
          <AttachmentIcon fileName={att.fileName} className="size-2.5" />
          {att.fileName}
        </span>
      ))}
    </div>
  );
}

/* ─── File-type Icon Helper ───────────────────────── */

const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
const TEXT_EXTS = ['.txt', '.md', '.json', '.xml', '.html', '.css', '.js', '.ts', '.py', '.yaml', '.yml'];
const SPREADSHEET_EXTS = ['.xlsx', '.xls', '.csv', '.tsv'];

function AttachmentIcon({ fileName, className = 'size-3' }: { fileName: string; className?: string }) {
  const ext = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
  if (IMAGE_EXTS.includes(ext)) return <Image className={className} />;
  if (TEXT_EXTS.includes(ext)) return <FileText className={className} />;
  if (SPREADSHEET_EXTS.includes(ext)) return <FileSpreadsheet className={className} />;
  return <FileIcon className={className} />;
}
